import tokens from '@kluein/design-tokens';
import { Anchor, Center, Overlay, Text, Tooltip } from '@mantine/core';
import { MagnifyingGlassPlus } from '@phosphor-icons/react';
import truncate from 'lodash/truncate';
import { useTranslation } from 'react-i18next';

import { KLUE_CARD_ZOOM_IMG } from 'lib/constants';
import TEST_IDS from 'test-ids';

export type ImageOverlayProps = {
  isHovered: boolean;
  isZoomed: boolean;
  isFullWidth?: boolean;
  zoom?: boolean;
  linkHref?: string;
  linkTarget?: string;
};

const URL_MAX_LENGTH = 350;

export default function ImageOverlay({
  isHovered,
  isZoomed,
  isFullWidth = false,
  zoom,
  linkHref,
  linkTarget = '_blank',
}: ImageOverlayProps) {
  const { t } = useTranslation();

  return zoom || linkHref ? (
    <Overlay
      color="#011627"
      opacity={isHovered && !isZoomed ? 1 : 0}
      className={zoom ? KLUE_CARD_ZOOM_IMG : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        cursor: zoom ? 'zoom-in' : 'default',
        transition: 'opacity 0.3s ease-in-out',
        // Add negative margin equivalent to card padding for full-width images
        margin: isFullWidth ? `0 -${tokens.spacing.large}` : undefined,
      }}
      data-test-id={TEST_IDS.klueCard.transform.image.overlay}
    >
      {zoom && (
        <MagnifyingGlassPlus
          size="2rem"
          color="white"
          data-test-id={TEST_IDS.klueCard.transform.image.zoom}
        />
      )}

      {linkHref && (
        <Tooltip
          label={
            <Text
              style={{
                display: 'block',
                wordBreak: 'break-all',
                textAlign: 'start',
              }}
              truncate="end"
            >
              {truncate(linkHref, { length: URL_MAX_LENGTH })}
            </Text>
          }
          position="bottom"
        >
          <Center
            bg="black"
            py="xs"
            px="sm"
            style={{
              position: 'absolute',
              bottom: '8px',
              borderRadius: '4px',
            }}
          >
            <Anchor
              href={linkHref}
              onClick={(event) => event.stopPropagation()}
              target={linkTarget}
              data-test-id={TEST_IDS.klueCard.transform.image.link}
              size="xs"
            >
              {t('Card:followLink')}
            </Anchor>
          </Center>
        </Tooltip>
      )}
    </Overlay>
  ) : null;
}
